import React, { useMemo, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';

/**
 * Selector de varias plantas para los reportes globales. `value` vacío = todas las plantas.
 * `plantas`: [{ _id | id, nombre }].
 */
const PlantasSelector = ({ plantas = [], value = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const [texto, setTexto] = useState('');
  const lista = useMemo(
    () =>
      plantas
        .map((p) => ({ id: String(p._id || p.id), nombre: String(p.nombre || '').trim() }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [plantas],
  );
  const visibles = lista.filter((p) => !texto || p.nombre.toLowerCase().includes(texto.toLowerCase()));
  const elegidas = new Set(value);
  const todas = value.length === 0;

  const alternar = (id) => {
    const sig = new Set(elegidas);
    if (sig.has(id)) sig.delete(id);
    else sig.add(id);
    // Marcar todas una por una equivale a "todas las plantas".
    onChange(sig.size === lista.length ? [] : [...sig]);
  };

  const etiqueta = todas
    ? 'Todas las plantas'
    : value.length === 1
      ? lista.find((p) => p.id === value[0])?.nombre || '1 planta'
      : `${value.length} plantas`;

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-10 min-w-[220px] flex items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 text-sm text-left"
        >
          <span className="truncate">{etiqueta}</span>
          <span className="flex items-center gap-1 shrink-0">
            {!todas && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Quitar filtro"
                className="rounded p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange([]);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    onChange([]);
                  }
                }}
              >
                <X className="h-3.5 w-3.5 text-gray-500" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="relative border-b p-2">
          <Search className="absolute left-4 top-4 h-3.5 w-3.5 text-gray-400" />
          <Input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Buscar planta" className="h-8 pl-7" />
        </div>
        <div className="max-h-72 overflow-y-auto py-1 text-sm">
          <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer font-medium">
            <input type="checkbox" checked={todas} onChange={() => onChange([])} />
            Todas las plantas
          </label>
          <div className="border-t my-1" />
          {visibles.map((p) => (
            <label key={p.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
              <input type="checkbox" checked={!todas && elegidas.has(p.id)} onChange={() => alternar(p.id)} />
              <span className="truncate">{p.nombre}</span>
            </label>
          ))}
          {visibles.length === 0 && <p className="px-3 py-2 text-gray-500">Sin coincidencias.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PlantasSelector;
